export type AnalyticsRecord = Record<string, unknown>;

/**
 * Property keys that carry personal data and must never leave the browser for
 * external analytics (ФЗ-152). Covers the URL/referrer family, the raw
 * user-agent, the client IP, and the browser/OS/device/screen/locale fields
 * that posthog-js attaches by default and which together form a device
 * fingerprint. IP-derived geolocation (`$geoip_*`) and first-touch attribution
 * (`$initial_*`) and the session-entry URL family (`$session_entry_*`, posthog-js
 * 1.39+) are matched by prefix in {@link isPiiPropertyKey}.
 *
 * Note: `$ip` and `$geoip_*` are added server-side by PostHog from the request
 * IP, so stripping them here is defence-in-depth - the authoritative fixes are
 * the project setting "Discard client IP data" plus the per-event
 * `$geoip_disable` flag.
 */
export const PII_PROPERTY_DENYLIST: readonly string[] = [
  "$ip",
  "$current_url",
  "$pathname",
  "$host",
  "$referrer",
  "$referring_domain",
  "$raw_user_agent",
  "$browser",
  "$browser_version",
  "$browser_language",
  "$browser_language_prefix",
  "$os",
  "$os_name",
  "$os_version",
  "$device_type",
  "$screen_height",
  "$screen_width",
  "$viewport_height",
  "$viewport_width",
  "$timezone",
  "$timezone_offset",
];

const PII_KEY_PREFIXES: readonly string[] = ["$geoip_", "$initial_", "$session_entry_"];

/** Control flag we inject to disable server-side geoip enrichment; not PII. */
const GEOIP_DISABLE_KEY = "$geoip_disable";

export function isPiiPropertyKey(key: string): boolean {
  if (key === GEOIP_DISABLE_KEY) {
    return false;
  }
  return (
    PII_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)) || PII_PROPERTY_DENYLIST.includes(key)
  );
}

/** Returns a new record with every personal-data key removed (input untouched). */
export function stripPiiProps<T extends AnalyticsRecord>(props: T): Partial<T> {
  const kept = Object.entries(props).filter(([key]) => !isPiiPropertyKey(key));
  return Object.fromEntries(kept) as Partial<T>;
}
